import { Controller } from "@hotwired/stimulus";
import Sortable from 'sortablejs';

// Generic drag-and-drop reorder controller.
//
// Usage:
//   <div data-controller="reorderable"
//        data-reorderable-url-value="/some/path/reorder"
//        data-reorderable-param-name-value="order">
//     <div data-reorderable-id="42">
//       <span data-reorderable-handle>::</span>
//       ...
//     </div>
//   </div>
//
// Each draggable child must have data-reorderable-id (the record id). Children
// without that attribute are ignored (so a "+ add new" form sibling is safe
// to leave inside the same container).
//
// On drag end, the controller PATCHes { [paramName]: [id1, id2, ...] } to the
// configured URL. The server is expected to persist the new positions.
export default class extends Controller {
  static values = {
    url:       String,
    paramName: { type: String, default: 'order' },
    confirmMessage: String,
    refreshOnFirstChange: { type: Boolean, default: false }
  };

  connect() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: '[data-reorderable-handle]',
      // Only items that opt in via data-reorderable-id participate.
      draggable: '[data-reorderable-id]',
      dataIdAttr: 'data-reorderable-id',
      ghostClass: 'reorderable-ghost',
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this)
    });
  }

  onStart() {
    const items = Array.from(this.element.querySelectorAll('[data-reorderable-id]'));
    this.initialFirstId = items[0]?.dataset.reorderableId;
    this.initialOrder = this.sortable.toArray();
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  async onEnd() {
    if (!this.urlValue) return;

    const items = Array.from(this.element.querySelectorAll('[data-reorderable-id]'));
    const order = items.map(item => item.dataset.reorderableId);

    // If a confirm message is provided and the first item has changed,
    // and that item was marked as important, ask for confirmation.
    if (this.confirmMessageValue && this.initialFirstId && order[0] !== this.initialFirstId) {
      const oldFirstItem = this.element.querySelector(`[data-reorderable-id="${this.initialFirstId}"]`);
      if (oldFirstItem && oldFirstItem.dataset.reorderableImportant === "true") {
        if (!confirm(this.confirmMessageValue)) {
          // Revert the DOM change by re-inserting items in their original order.
          this.sortable.sort(this.initialOrder, true);
          return;
        }
      }
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ [this.paramNameValue]: order })
      });
      if (!response.ok) {
        console.error('Reorder request failed', response.status);
      } else if (this.refreshOnFirstChangeValue && this.initialFirstId && order[0] !== this.initialFirstId) {
        // If the first item changed and we're configured to refresh, do a Turbo visit
        // to pick up the new "first photo" annotations in the callouts section.
        if (window.Turbo) {
          window.Turbo.visit(window.location.href, { action: "replace" });
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Reorder request error', err);
    }
  }
}
